// import node module libraries
import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Row, Col, Card, ProgressBar, Dropdown, ListGroup } from 'react-bootstrap';
import { ExclamationCircleFill, CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons';
import axios from 'axios';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import bootstrap icons
import { Calendar4, Clock, AppIndicator, App } from 'react-bootstrap-icons';

// import sub components
import OffcanvasUpdateFlowConfigOption from './configuration/OffcanvasUpdateFlowConfigOption';
import ModalDeleteConfiguration from './configuration/ModalDeleteConfiguration';
import ModalResetConfiguration from './configuration/ModalResetConfiguration';

// import custom components
import ConfigurationStepWithProgress from 'components/dashboard/common/steps/ConfigurationStepWithProgress';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// import translations
import { useTranslation } from 'react-i18next';

const ConfigurationSection = ({selectedFlow, item, onUpdate}) => {
	const { t } = useTranslation();
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	
	//
	// Load configuration options dynamically
	//

	// State for configuration options
	const [configurationOptions, setConfigurationOptions] = useState(null);
	// Load the configuration for the selected section dynamically
	useEffect(() => {
		const loadConfigurationOptions = async () => {
			try {
				// Ermittle die Konfiguration basierend auf dem Flow config_key
				const flowConfigKey = item?.config_key?.split('.').slice(0, 2).join('.') || 'undefined';
				const configModule = await import(`data/dashboard/marketplace/configs/${flowConfigKey}.js`);
				setConfigurationOptions(configModule.default || null);
			} catch (error) {
				console.error('Error loading flow configuration options:', error);
				setConfigurationOptions(null);
			}
		};

		loadConfigurationOptions();
	}, [item]);

	//
	// Offcanvas
	//

	// State for Offcanvas
	const [showOffcanvasUpdateFlowConfigurationOption, setShowOffcanvasUpdateFlowConfigurationOption] = useState(false);
	// Handlers for Offcanvas
	const handleCloseOffcanvasUpdateFlowConfigurationOption = () => setShowOffcanvasUpdateFlowConfigurationOption(false);
	const handleShowOffcanvasUpdateFlowConfigurationOption = () => setShowOffcanvasUpdateFlowConfigurationOption(true);

	//
	// Modal
	//

	// Handler for showing configuration action modals
	const handleConfigClick = (action) => {
		if (action === 'reset') {
			setShowResetModal(true);
		} else if (action === 'delete') {
			setShowDeleteModal(true);
		}
	};

	//
	// Updating Configuration Values -> Form Values
	//

	// State for form values - Change to object format to match database
	const [formValues, setFormValues] = useState(item.config_value.configuration_units || {});

	// Handler for updating form values - Update to work with object structure
	const handleUpdateFormValues = (e) => {
		const { name, value, type, checked } = e.target;
		const newValue = type === 'checkbox' ? checked : value;

		// Update the form values state using object structure
		setFormValues(prevFormValues => {
			const updatedValues = { ...prevFormValues };

			// Initialize unit if it doesn't exist
			if (!updatedValues[selectedRow]) {
				updatedValues[selectedRow] = {
					id: selectedRow,
					configuration: {},
					progress: 100
				};
			}

			// Update the configuration field
			updatedValues[selectedRow] = {
				...updatedValues[selectedRow],
				configuration: {
					...updatedValues[selectedRow].configuration,
					[name]: newValue,
				},
				progress: 100
			};

			return updatedValues;
		});
	};

	// Function to calculate the configuration status and progress - Update to work with object structure
	const getConfigurationStatusAndProgress = () => {
		if (!configurationOptions || !configurationOptions.sections) {
			return { status: 'new', progress: 0 };
		}

		const units = configurationOptions.sections;
		const totalUnits = Object.keys(units).length;

		// Count completed units from object structure
		const completed = Object.values(formValues).filter(unit => unit.progress === 100).length;

		if (completed === 0) {
			return { status: 'new', progress: 0 };
		}

		const progress = Math.round((completed / totalUnits) * 100);

		let status = 'configuration';
		if (completed === 0) status = 'new';
		else if (completed === totalUnits) status = 'ready';

		return { status, progress };
	};

	const auth = useAuthUser(); // Assuming this gets the auth user data

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	//
	// Updating Configuration
	//

	// State for configuration item
	const [configurationItem, setConfigurationItem] = useState(item);
	
	// Update Flow Configuration Option
	const [selectedRow, setSelectedRow] = useState({});
	const [isLoadingButtonUpdateFlowConfigurationOption, setIsLoadingButtonUpdateFlowConfigurationOption] = useState(false);
	const handleUpdateFlowConfigurationOption = async (e) => {
		e.preventDefault();
		setIsLoadingButtonUpdateFlowConfigurationOption(true);

		// 1. Alle befüllten Felder aus dem Formular holen
		const form = e.target;

		const updatedFields = {};
		for (let i = 0; i < form.elements.length; i++) {
			const el = form.elements[i];
			if (!el.name) continue;
			if (el.type === 'checkbox') {
				updatedFields[el.name] = el.checked;
			} else {
				updatedFields[el.name] = el.value;
			}
		}

		// Remove button types from updatedFields
		const filteredFields = Object.fromEntries(
			Object.entries(updatedFields).filter(([key, value]) => {
				const el = form.elements.namedItem(key);
				return el && el.type !== 'button' && el.type !== 'submit' && el.type !== 'reset';
			})
		);

		// 2. formValues für die aktuelle Unit aktualisieren (using object structure)
		const updatedUnits = { ...formValues };

		if (!updatedUnits[selectedRow]) {
			updatedUnits[selectedRow] = {
				id: selectedRow,
				configuration: {},
				progress: 100
			};
		}

		updatedUnits[selectedRow] = {
			...updatedUnits[selectedRow],
			configuration: {
				...updatedUnits[selectedRow].configuration,
				...filteredFields
			},
			progress: 100
		};

		setFormValues(updatedUnits);

		// 3. Status und Progress berechnen basierend auf updatedUnits
		const StatusAndProgress = (() => {
			if (!configurationOptions || !configurationOptions.sections) {
				return { status: 'new', progress: 0 };
			}
			const units = configurationOptions.sections;
			const totalUnits = Object.keys(units).length;
			const completed = Object.values(updatedUnits).filter(unit => unit.progress === 100).length;

			if (completed === 0) {
				return { status: 'new', progress: 0 };
			}

			const progress = Math.round((completed / totalUnits) * 100);
			let status = 'configuration';
			if (completed === 0) status = 'new';
			else if (completed === totalUnits) status = 'ready';

			return { status, progress };
		})();

		// 4. Daten für das Backend vorbereiten (no merging needed - use updatedUnits directly)
		const data = {
			...item,
			config_value: {
				...item.config_value,
				status: StatusAndProgress.status,
				progress: StatusAndProgress.progress,
				configuration_units: updatedUnits
			}
		};

		// 5. API Call
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.patch(`${baseURL}/tenants/${auth.tenant_id}/flows/configs/${item.id}`, data, options)
			.then(response => {
				const responseData = response.data;
				setConfigurationItem(responseData.result);
				onUpdate(responseData.result);
				setIsLoadingButtonUpdateFlowConfigurationOption(false);
				handleCloseOffcanvasUpdateFlowConfigurationOption();
				handleShowNotification(setNotification, 'success', t('Success'), responseData.message);
			})
			.catch((error) => {
				setIsLoadingButtonUpdateFlowConfigurationOption(false);
				handleCloseOffcanvasUpdateFlowConfigurationOption();
				handleShowNotification(setNotification, 'danger', t('Error'), t(error.message));
			});

	};

	//
	// Reset Configuration
	//

	// Reset Flow Configuration
	const [showResetModal, setShowResetModal] = useState(false); // State for showing modal
	const [isLoadingButtonResetConfiguration, setIsLoadingButtonResetConfiguration] = useState(null); // State for loading spinner

	const handleConfirmReset = async (e) => {
		setIsLoadingButtonResetConfiguration(true);

		// Prepare the data to be sent
		const data = item = {
			...item,
			config_value: {
				status: 'new',
				progress: 0,
				configuration_units: {}
			}
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/tenants/${auth.tenant_id}/flows/configs/${item.id}`, data, options)
			.then(response => {
				const responseData = response.data; // Extract the data from the response
				setConfigurationItem(responseData.result);
				// Update Callback to Parent
				onUpdate(responseData.result);
				setFormValues({}); // Reset form values
				setIsLoadingButtonResetConfiguration(false);
				setShowResetModal(false);
				handleShowNotification(setNotification, 'success', t('Successful'), t('Configuration resetted successfully'));
				setTimeout(() => {
					window.location.reload(); // Reload the page to reflect changes
				}
				, 2000);
			})
			.catch((error) => {
				setIsLoadingButtonResetConfiguration(false);
				setShowResetModal(false);
				handleShowNotification(setNotification, 'danger', t('Error'), t(error.message));
			});

	};

	//
	// Delete Configuration
	//

	// Delete Flow Configuration
	const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing modal
	const [isLoadingButtonDeleteConfiguration, setIsLoadingButtonDeleteConfiguration] = useState(null); // State for loading spinner
	
	const handleConfirmDelete = async () => {
		setIsLoadingButtonDeleteConfiguration(true);

		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.delete(`${baseURL}/tenants/${auth.tenant_id}/flows/configs/${item.id}`, options)
			.then(response => {
				// Update the quidedata state with the deleted data
				setIsLoadingButtonDeleteConfiguration(false);
				setShowDeleteModal(false);
				handleShowNotification(setNotification, 'success', t('Successful'), t('Configuration deleted successfully'));

				setTimeout(() => {
					window.location.href = '/dashboard/marketplace/flows';
				}, 2000);
			})
			.catch((error) => {
				setIsLoadingButtonDeleteConfiguration(false);
				setShowDeleteModal(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	// The forwardRef is important!!
	// Dropdown needs access to the DOM node in order to position the Menu
	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to=""
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));
	const ActionMenu = () => {
		return (
			<div>
				<Dropdown>
					<Dropdown.Toggle as={CustomToggle}>
						<i className="fe fe-more-vertical text-muted"></i>
					</Dropdown.Toggle>
					<Dropdown.Menu align="end">
						<Dropdown.Header>{t('Action')}</Dropdown.Header>
						<Dropdown.Item eventKey="1" onClick={() => handleConfigClick('reset')}>{t('Reset')}</Dropdown.Item>
						<Dropdown.Item eventKey="2" onClick={() => handleConfigClick('delete')}>{t('Delete')}</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown>
			</div>
		);
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Card>
				<Card.Header className="card-header">
					<div className="d-flex justify-content-between align-items-center">
						<div>
							<h4 className="mb-0">{t('Flow Configuration')}</h4>
						</div>
						<div>
							<ActionMenu />
						</div>
					</div>
				</Card.Header>
				<Card.Body className="card-body">
					<ListGroup variant="flush">
						<ListGroup.Item className="px-0">
							<div className="d-flex justify-content-between align-items-center">
								<div className="d-flex align-items-center">
									<App size={16} className="text-primary" />
									<div className="ms-2">
										<h5 className="mb-0 text-body">{t('Configration ID')}</h5>
									</div>
								</div>
								<div>
									<p className="text-dark mb-0 fw-semi-bold">{configurationItem.id}</p>
								</div>
							</div>
						</ListGroup.Item>
						<ListGroup.Item className="px-0">
							<div className="d-flex justify-content-between align-items-center">
								<div className="d-flex align-items-center">
									<App size={16} className="text-primary" />
									<div className="ms-2">
										<h5 className="mb-0 text-body">{t('Configration Key')}</h5>
									</div>
								</div>
								<div>
									<p className="text-dark mb-0 fw-semi-bold">{configurationItem.config_key}</p>
								</div>
							</div>
						</ListGroup.Item>
						<ListGroup.Item className="px-0">
							<div className="d-flex justify-content-between align-items-center">
								<div className="d-flex align-items-center">
									<Calendar4 size={16} className="text-primary" />
									<div className="ms-2">
										<h5 className="mb-0 text-body">{t('Created at')}</h5>
									</div>
								</div>
								<div>
									<p className="text-dark mb-0 fw-semi-bold">{new Date(configurationItem.config_timestamp_created).toLocaleString()}</p>
								</div>
							</div>
						</ListGroup.Item>
						<ListGroup.Item className="px-0">
							<div className="d-flex justify-content-between align-items-center">
								<div className="d-flex align-items-center">
									<Calendar4 size={16} className="text-primary" />
									<div className="ms-2">
										<h5 className="mb-0 text-body">{t('Updated at')}</h5>
									</div>
								</div>
								<div>
									<p className="text-dark mb-0 fw-semi-bold">{new Date(configurationItem.config_timestamp_last_update).toLocaleString()}</p>
								</div>
							</div>
						</ListGroup.Item>
						<ListGroup.Item className="px-0">
							<div className="d-flex justify-content-between align-items-center">
								<div className="d-flex align-items-center">
									<AppIndicator size={16} className="text-primary" />
									<div className="ms-2">
										<h5 className="mb-0 text-body">{t('Status')}</h5>
									</div>
								</div>
								<div>
									<p className="text-dark mb-0 fw-semi-bold">
										{t(
											configurationItem.config_value.status
												? configurationItem.config_value.status.charAt(0).toUpperCase() +
												  configurationItem.config_value.status.slice(1)
												: ''
										)}
									</p>
								</div>
							</div>
						</ListGroup.Item>
					</ListGroup>
				</Card.Body>
				<Card.Footer className="card-footer pt-5">
					<Row>
						{configurationOptions === null ? (
							<Col className="text-center">
								<Alert variant="warning" className="bg-warning rounded">
									<ExclamationTriangleFill className="me-2" />
									{t('No configuration options available for this Flow.')}{' '}{t('Please contact helpdesk and provide the following information:')}
									<br />
									<br />
									<strong>{t('Flow ID:')}</strong> {configurationItem.id} <br />
									<strong>{t('Configuration ID:')}</strong> {configurationItem.config_key} <br />
								</Alert>
							</Col>
						) : (
							// else display configuration options
							<>
								{configurationOptions?.sections &&
									Object.entries(configurationOptions.sections).map(([sectionKey, option], idx) => (
										<Col
											lg={4}
											md={12}
											xs={12}
											className={`${idx !== 0 && idx % 3 !== 0 ? 'border-start-lg' : ''} pb-6`}
											key={sectionKey}
										>
											
											<ConfigurationStepWithProgress
												id={sectionKey}
												title={`${t(option.title)}`}
												subtitle={t(option.description)}
												onClick={() => {
													setSelectedRow(sectionKey);
													handleShowOffcanvasUpdateFlowConfigurationOption();
												}}
												progress={
													Object.values(configurationItem.config_value.configuration_units || {}).find(
														config => config.id === sectionKey
													)?.progress || 0
													
												}
												progressVariant={''}
											/>
										</Col>
									))}
							</>
						)}
					</Row>
					<Row className="mt-8">
						<Col className="text-center">
							{t('Do you need help with the configuration?')}{' '}
							<Link target="_blank" to="/marketing/help-center/guide" className="text-decoration-none ms-2">
								{t('Help Center Guide')}<i className="fe fe-arrow-up-right ms-1"></i>
							</Link>
						</Col>
					</Row>
				</Card.Footer>
			</Card>
			{/* Update Flow Configuration Option Offcanvas */}
			<OffcanvasUpdateFlowConfigOption
				show={showOffcanvasUpdateFlowConfigurationOption}
				onHide={handleCloseOffcanvasUpdateFlowConfigurationOption}
				flow={item}
				selectedRow={selectedRow}
				formValues={formValues}
				onChange={handleUpdateFormValues}
				onSubmit={handleUpdateFlowConfigurationOption}
				isLoadingButton={isLoadingButtonUpdateFlowConfigurationOption}
			/>
			{/* Delete Configration Modal */}
			<ModalResetConfiguration
				show={showResetModal}
				onHide={() => setShowResetModal(false)}
				handleConfirmReset={handleConfirmReset}
				isLoadingButton={isLoadingButtonResetConfiguration}
			/>
			{/* Delete Configration Modal */}
			<ModalDeleteConfiguration
				show={showDeleteModal}
				onHide={() => setShowDeleteModal(false)}
				selectedFlow={selectedFlow.marketplace_value}
				handleConfirmDelete={handleConfirmDelete}
				isLoadingButton={isLoadingButtonDeleteConfiguration}
			/>
		</Fragment>
	);
};
export default ConfigurationSection;
